import { useCallback, useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { getMapLayer, getObject } from "@/api/objects";

const CLICK_RADIUS = 7;
const CLUSTER_LAYER_ID = "h3-clusters-circle";

const GEO_INTERACTIVE_LAYER_IDS = [
  "geo-points",
  "geo-polygons-fill",
  "geo-polygons-outline",
];

export const useMapInteractions = ({
  data,
  message,
  filters,
  panelWidth = 0,
  setIsRightPanelOpen,
  setIsPanelMinimized,
  setIsObjectOpen,
  setIsSearchOpen,
  setCurrentObject,
  setMapClickResults,
  bbox,
}) => {
  const mapRef = useRef(null);
  const panelWidthRef = useRef(panelWidth);
  const isDraggingRef = useRef(false);
  const hoveringFeatureRef = useRef(false);
  const interactiveLayerIdsRef = useRef([]);
  const mouseMoveRafRef = useRef(null);
  const lastMouseMoveEventRef = useRef(null);
  const cursorRef = useRef(null);
  const [objectPending, setObjectPending] = useState(false);
  const [resultsPending, setResultPending] = useState(false);

  const dataRef = useRef(null);
  const bboxRef = useRef(null);
  const filtersRef = useRef(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    bboxRef.current = bbox;
  }, [bbox]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  const getMapRequestFilters = useCallback(() => {
    const filters = filtersRef.current;
    const definitionUuids = filters?.definition_uuid;
    const hasDefinitions =
      Array.isArray(definitionUuids) && definitionUuids.length > 0;

    const search =
      typeof filters.search === "string" ? filters.search.trim() : "";
    const searchInSelectedDefinitions =
      filters.search_in_selected_definitions !== false;

    if (!hasDefinitions && !search) return null;
    if (!hasDefinitions && searchInSelectedDefinitions) return null;

    const requestFilters = {};

    if (hasDefinitions) {
      requestFilters.definitions_uuids = definitionUuids;
    }

    if (search) {
      requestFilters.search = search;
    }

    return requestFilters;
  }, []);

  const setCanvasCursor = useCallback((map, cursor) => {
    if (cursorRef.current === cursor) return;

    cursorRef.current = cursor;
    map.getCanvas().style.cursor = cursor;
  }, []);

  const updateInteractiveLayerIds = useCallback((map) => {
    const geoLayerIds = GEO_INTERACTIVE_LAYER_IDS.filter((layerId) =>
      map.getLayer(layerId),
    );

    const clusterLayerIds = [CLUSTER_LAYER_ID].filter((layerId) =>
      map.getLayer(layerId),
    );

    interactiveLayerIdsRef.current = [...geoLayerIds, ...clusterLayerIds];
  }, []);

  const zoomToObjects = useCallback(
    (items = [], panelWidthOverride = null) => {
      const map = mapRef.current;
      if (!map || !items.length) return;

      const width = panelWidthOverride ?? panelWidthRef.current;

      const bounds = new maplibregl.LngLatBounds();
      let hasCoordinates = false;

      items.forEach(({ definition, object }) => {
        definition?.fields?.forEach((field) => {
          const value = object?.[field.field_name];
          if (!value) return;

          if (field.type === "point") {
            const { lat, lon } = value;
            if (typeof lat === "number" && typeof lon === "number") {
              bounds.extend([lon, lat]);
              hasCoordinates = true;
            }
          }

          if (field.type === "parent_polygon" && value?.center) {
            const { lat, lon } = value.center;

            if (typeof lat === "number" && typeof lon === "number") {
              bounds.extend([lon, lat]);
              hasCoordinates = true;
            }
          }

          if (field.type === "polygon" || field.type === "parent_polygon") {
            if (Array.isArray(value)) {
              value.forEach(({ lat, lon }) => {
                if (typeof lat === "number" && typeof lon === "number") {
                  bounds.extend([lon, lat]);
                  hasCoordinates = true;
                }
              });
            }

            if (value?.polygons?.length) {
              value.polygons.forEach((polygon) => {
                polygon.forEach(({ lat, lon }) => {
                  if (typeof lat === "number" && typeof lon === "number") {
                    bounds.extend([lon, lat]);
                    hasCoordinates = true;
                  }
                });
              });
            }
          }
        });
      });

      if (!hasCoordinates) return;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const isSinglePoint = sw.lat === ne.lat && sw.lng === ne.lng;

      if (isSinglePoint) {
        map.flyTo({
          center: [sw.lng, sw.lat],
          zoom: 16,
          duration: 800,
        });
      } else {
        map.fitBounds(bounds, {
          padding: {
            top: 80,
            bottom: 80,
            right: width ? width + 40 : 80,
            left: 80,
          },
          duration: 800,
        });
      }
    },
    [mapRef],
  );

  const zoomToClusters = useCallback(
    (clusters = [], panelWidthOverride = null) => {
      const map = mapRef.current;
      if (!map || !clusters.length) return;

      const width = panelWidthOverride ?? panelWidthRef.current;

      const bounds = new maplibregl.LngLatBounds();
      let hasCoordinates = false;

      clusters.forEach((cluster) => {
        const clusterBounds = cluster.h3Bounds;
        const center = cluster.center;

        if (
          clusterBounds &&
          typeof clusterBounds.minLon === "number" &&
          typeof clusterBounds.minLat === "number" &&
          typeof clusterBounds.maxLon === "number" &&
          typeof clusterBounds.maxLat === "number"
        ) {
          bounds.extend([clusterBounds.minLon, clusterBounds.minLat]);
          bounds.extend([clusterBounds.maxLon, clusterBounds.maxLat]);
          hasCoordinates = true;
          return;
        }

        if (
          center &&
          typeof center.lon === "number" &&
          typeof center.lat === "number"
        ) {
          bounds.extend([center.lon, center.lat]);
          hasCoordinates = true;
        }
      });

      if (!hasCoordinates) return;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const isSinglePoint = sw.lat === ne.lat && sw.lng === ne.lng;

      if (isSinglePoint) {
        map.flyTo({
          center: [sw.lng, sw.lat],
          zoom: Math.max(map.getZoom(), 13),
          duration: 800,
        });
      } else {
        map.fitBounds(bounds, {
          padding: {
            top: 80,
            bottom: 80,
            right: width ? width + 40 : 80,
            left: 80,
          },
          duration: 800,
          maxZoom: 14,
        });
      }
    },
    [],
  );

  const fetchObject = useCallback(
    async (def_uuid, uuid) => {
      setObjectPending(true);

      try {
        const obj = await getObject(def_uuid, uuid);
        setCurrentObject(obj);
      } catch (error) {
        if (message) {
          message.open({
            type: "error",
            content: error.message,
            duration: 10,
          });
        }
      } finally {
        setObjectPending(false);
      }
    },
    [message, setCurrentObject],
  );

  const onMapLoad = useCallback(
    (event) => {
      if (!event?.target) return;

      const map = event.target;
      mapRef.current = map;
      updateInteractiveLayerIds(map);

      const handleClick = async (e) => {
        const map = mapRef.current;
        if (!map) return;

        const requestFilters = getMapRequestFilters();
        if (!requestFilters) return;

        const clusterFeatures = map.queryRenderedFeatures(e.point, {
          layers: [CLUSTER_LAYER_ID].filter((layerId) => map.getLayer(layerId)),
        });

        const clusterFeature = clusterFeatures[0];

        if (clusterFeature) {
          const h3Index = clusterFeature.properties?.h3Index;

          if (!h3Index) return;

          setResultPending(true);

          try {
            const res = await getMapLayer({
              ...requestFilters,
              bbox: JSON.stringify(bboxRef.current),
              zoom: Math.floor(map.getZoom()),
              h3Index,
            });

            const objects = res.objects ?? [];

            setMapClickResults(objects);
            setIsObjectOpen(false);
            setIsSearchOpen(true);
            setIsRightPanelOpen(true);
            setIsPanelMinimized(false);
          } catch (error) {
            message?.open({
              type: "error",
              content: error.message,
              duration: 10,
            });
          } finally {
            setResultPending(false);
          }

          return;
        }

        setResultPending(true);

        const p1 = map.unproject([
          e.point.x - CLICK_RADIUS,
          e.point.y - CLICK_RADIUS,
        ]);

        const p2 = map.unproject([
          e.point.x + CLICK_RADIUS,
          e.point.y + CLICK_RADIUS,
        ]);

        const point = {
          minLon: p1.lng,
          minLat: p1.lat,
          maxLon: p2.lng,
          maxLat: p2.lat,
        };

        try {
          const res = await getMapLayer({
            ...requestFilters,
            bbox: JSON.stringify(bboxRef.current),
            point: JSON.stringify(point),
            zoom: Math.floor(map.getZoom()),
          });

          const objects = res.objects ?? [];

          if (!objects.length) {
            setMapClickResults(null);
            setIsObjectOpen(false);
            setIsSearchOpen(true);

            return;
          }

          if (objects.length === 1) {
            setMapClickResults(null);
            const obj = objects[0];

            setIsObjectOpen(true);

            fetchObject(obj.definition.uuid, obj.object.uuid);
          } else {
            setMapClickResults(objects);
          }

          setIsSearchOpen(true);
          setIsRightPanelOpen(true);
          setIsPanelMinimized(false);
        } catch (error) {
          message?.open({
            type: "error",
            content: error.message,
            duration: 10,
          });
        } finally {
          setResultPending(false);
        }
      };

      const handleMouseMove = (e) => {
        lastMouseMoveEventRef.current = e;

        if (mouseMoveRafRef.current) return;

        mouseMoveRafRef.current = requestAnimationFrame(() => {
          mouseMoveRafRef.current = null;

          const map = mapRef.current;
          const event = lastMouseMoveEventRef.current;

          if (!map || !event) return;

          if (isDraggingRef.current) {
            setCanvasCursor(map, "grabbing");
            return;
          }

          let interactiveLayerIds = interactiveLayerIdsRef.current.filter(
            (layerId) => map.getLayer(layerId),
          );

          if (!interactiveLayerIds.length) {
            updateInteractiveLayerIds(map);

            interactiveLayerIds = interactiveLayerIdsRef.current.filter(
              (layerId) => map.getLayer(layerId),
            );
          }

          if (!interactiveLayerIds.length) {
            hoveringFeatureRef.current = false;
            setCanvasCursor(map, "grab");
            return;
          }

          const features = map.queryRenderedFeatures(event.point, {
            layers: interactiveLayerIds,
          });

          const isOverInteractiveLayer = features.length > 0;

          hoveringFeatureRef.current = isOverInteractiveLayer;

          setCanvasCursor(map, isOverInteractiveLayer ? "pointer" : "grab");
        });
      };

      map.on("click", handleClick);
      map.on("mousemove", handleMouseMove);

      map.on("dragstart", () => {
        isDraggingRef.current = true;
        setCanvasCursor(map, "grabbing");
      });
      map.on("dragend", () => {
        isDraggingRef.current = false;
        setCanvasCursor(map, hoveringFeatureRef.current ? "pointer" : "grab");
      });
      map.on("styledata", () => {
        updateInteractiveLayerIds(map);
      });

      setCanvasCursor(map, "grab");
    },
    [
      setIsObjectOpen,
      setIsRightPanelOpen,
      fetchObject,
      message,
      setMapClickResults,
      setIsSearchOpen,
      setIsPanelMinimized,
      setCanvasCursor,
      updateInteractiveLayerIds,
      getMapRequestFilters,
    ],
  );

  return {
    onMapLoad,
    mapRef,
    objectPending,
    resultsPending,
    fetchObject,
    zoomToObjects,
    zoomToClusters,
  };
};

export default useMapInteractions;
